import React, { memo } from 'react';
import DashboardCardIconHeader from './DashboardCardIconHeader';
import dashboardCardSx from './DashboardCard.styles';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';

export type DashboardCardSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type DashboardCardColor =
    | 'grey'
    | 'gray'
    | 'blue'
    | 'orange'
    | 'indigo'
    | 'red'
    | 'pink'
    | 'purple'
    | 'deepPurple'
    | 'lightBlue'
    | 'cyan'
    | 'teal'
    | 'green'
    | 'lightGreen'
    | 'lime'
    | 'yellow'
    | 'amber'
    | 'deepOrange'
    | 'brown'
    | 'blueGrey';

export type DashboardCardProps = {
    size?: DashboardCardSize;
    color?: DashboardCardColor;
    variant?: 'icon' | 'full';
    headerFullWidth?: boolean;
    headerContent?: React.ReactNode;
    icon?: React.ReactNode;
    topContent?: React.ReactNode;
    topLabel?: React.ReactNode;
    children?: React.ReactNode;
    darkMode?: boolean;
};

const DashboardCard = memo(
    ({
        size = 'xl',
        color = 'grey',
        variant = 'icon',
        headerFullWidth = false,
        headerContent,
        icon = <div />,
        topContent = '',
        topLabel,
        children,
        darkMode = false,
    }: DashboardCardProps) => {
        const fullWidth = variant === 'full';
        const theme = useTheme();
        const sx = dashboardCardSx(theme);

        const containerStyle = {
            ...sx.container,
            ...(size === 'xs' ? sx.containerXSmall : {}),
            ...(size === 'sm' ? sx.containerSmall : {}),
            ...(size === 'md' ? sx.containerMedium : {}),
            ...(size === 'lg' ? sx.containerLarge : {}),
            ...(size === 'xl' ? sx.containerXLarge : {}),
        };

        const headerBase = {
            ...sx.headerContainer,
            ...(!fullWidth ? sx.iconFloat : {}),
            ...(fullWidth ? sx.fullWidth : {}),
            ...(fullWidth ? sx.labelMargin : {}),
        };

        const colorMap: Record<DashboardCardColor, object> = {
            grey: sx.backgroundGrey,
            gray: sx.backgroundGrey,
            blue: sx.backgroundBlue,
            orange: sx.backgroundOrange,
            indigo: sx.backgroundIndigo,
            red: sx.backgroundRed,
            pink: sx.backgroundPink,
            purple: sx.backgroundPurple,
            deepPurple: sx.backgroundDeepPurple,
            lightBlue: sx.backgroundLightBlue,
            cyan: sx.backgroundCyan,
            teal: sx.backgroundTeal,
            green: sx.backgroundGreen,
            lightGreen: sx.backgroundLightGreen,
            lime: sx.backgroundLime,
            yellow: sx.backgroundYellow,
            amber: sx.backgroundAmber,
            deepOrange: sx.backgroundDeepOrange,
            brown: sx.backgroundBrown,
            blueGrey: sx.backgroundBlueGrey,
        };

        const computedHeaderSx = {
            ...headerBase,
            ...(colorMap[color] || {}),
        };

        return (
            <Box sx={containerStyle}>
                <Box sx={darkMode ? sx.innerContainerDarker : sx.innerContainer}>
                    <Box sx={sx.topContentRightAlign}>
                        <Box sx={computedHeaderSx}>
                            {!fullWidth && <DashboardCardIconHeader icon={icon} />}
                            {fullWidth && headerContent}
                        </Box>

                        {!fullWidth && (
                            <Box>
                                <Box component="h3" sx={sx.topContentRightAlignText}>
                                    {topContent}
                                </Box>
                                {Boolean(topLabel) && typeof topLabel === 'string' && (
                                    <Box component="p" sx={sx.topContentRightAlignLabel}>
                                        {topLabel}
                                    </Box>
                                )}
                                {Boolean(topLabel) && typeof topLabel !== 'string' && (
                                    <Box component="span" sx={sx.topContentRightAlignLabel}>
                                        {topLabel}
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Box sx={{ ...sx.contentWrapper, ...sx.contentSeparator }}>
                            <Box sx={sx.content}>{children}</Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }
);

export default DashboardCard;
